import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  IconButton
} from '@mui/material';
import {
  Save as SaveIcon,
  DirectionsRun as ActiveIcon,
  DirectionsWalk as WalkIcon,
  Weekend as RestIcon,
  Hotel as BedIcon,
  LocalHospital as HospitalIcon,
  Assessment as AssessmentIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useUserData } from '../../hooks/useUserData';
import { createClinicalAssessment } from '../../services/oncologyService';
import { ECOG_STATUSES, KARNOFSKY_SCORES, ECOG_STATUS_DESCRIPTIONS, KARNOFSKY_SCORE_DESCRIPTIONS } from '../../types/enums';
import { PageHeader, ContentCard } from './oncologyStyles';

const ecogIcons = {
  '0': <ActiveIcon />,
  '1': <WalkIcon />,
  '2': <RestIcon />,
  '3': <BedIcon />,
  '4': <HospitalIcon />,
  '5': <HospitalIcon />
};

const ecogColors = {
  '0': 'success',
  '1': 'info',
  '2': 'warning',
  '3': 'error',
  '4': 'error',
  '5': 'default'
};

const PerformanceStatusAssessment = ({ patientId: propPatientId, encounterId, onSave, onCancel, embedded = false }) => {
  const { patientId: urlPatientId } = useParams();
  const navigate = useNavigate();
  const patientId = propPatientId || urlPatientId;
  const { defaultFacility } = useUserData();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [ecogStatus, setEcogStatus] = useState('');
  const [karnofskyScore, setKarnofskyScore] = useState('');
  const [notes, setNotes] = useState('');

  const handleSubmit = async () => {
    if (!ecogStatus && !karnofskyScore) {
      setError('Please select at least one performance status score');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const assessmentData = {
        patientId,
        facilityId: defaultFacility.id,
        encounterId,
        ecogStatus: ecogStatus || undefined,
        karnofskyScore: karnofskyScore || undefined,
        assessmentDate: new Date().toISOString(),
        notes
      };

      const result = await createClinicalAssessment(assessmentData);
      setSuccess(true);

      if (onSave) {
        onSave(result);
      }
    } catch (err) {
      console.error('Error saving assessment:', err);
      setError(err.message || 'Failed to save assessment');
    } finally {
      setLoading(false);
    }
  };

  // Convert ECOG to approximate Karnofsky
  const getApproximateKarnofsky = (ecog) => {
    const mapping = {
      '0': '100',
      '1': '80',
      '2': '60',
      '3': '40',
      '4': '20',
      '5': '0'
    };
    return mapping[ecog];
  };

  // Convert Karnofsky to approximate ECOG
  const getApproximateEcog = (kps) => {
    const kpsNum = parseInt(kps);
    if (kpsNum >= 90) return '0';
    if (kpsNum >= 70) return '1';
    if (kpsNum >= 50) return '2';
    if (kpsNum >= 30) return '3';
    if (kpsNum >= 10) return '4';
    return '5';
  };

  const handleEcogSelect = (ecog) => {
    setEcogStatus(ecog);
    // Auto-suggest Karnofsky if not set
    if (!karnofskyScore) {
      setKarnofskyScore(getApproximateKarnofsky(ecog));
    }
  };

  const handleKarnofskySelect = (kps) => {
    setKarnofskyScore(kps);
    // Auto-suggest ECOG if not set
    if (!ecogStatus) {
      setEcogStatus(getApproximateEcog(kps));
    }
  };

  // Form content that can be reused in different layouts
  const formContent = (
    <>
      {/* ECOG Performance Status */}
      <Typography variant="h6" gutterBottom>
        ECOG Performance Status
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {ECOG_STATUSES.map((ecog) => (
          <Grid item xs={12} sm={6} md={4} key={ecog}>
            <Card 
              variant={ecogStatus === ecog ? 'elevation' : 'outlined'}
              sx={{ 
                borderColor: ecogStatus === ecog ? `${ecogColors[ecog]}.main` : 'divider',
                borderWidth: ecogStatus === ecog ? 2 : 1
              }}
            >
              <CardActionArea onClick={() => handleEcogSelect(ecog)}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Chip 
                      icon={ecogIcons[ecog]}
                      label={`ECOG ${ecog}`}
                      color={ecogStatus === ecog ? ecogColors[ecog] : 'default'}
                      variant={ecogStatus === ecog ? 'filled' : 'outlined'}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {ECOG_STATUS_DESCRIPTIONS[ecog]}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Karnofsky Performance Scale */}
      <Typography variant="h6" gutterBottom>
        Karnofsky Performance Scale (KPS)
      </Typography>
      <Grid container spacing={1} sx={{ mb: 4 }}>
        {KARNOFSKY_SCORES.map((kps) => {
          const kpsNum = parseInt(kps);
          let color = 'success';
          if (kpsNum <= 40) color = 'error';
          else if (kpsNum <= 60) color = 'warning';
          else if (kpsNum <= 80) color = 'info';

          return (
            <Grid item xs={6} sm={4} md={2.4} key={kps}>
              <Card 
                variant={karnofskyScore === kps ? 'elevation' : 'outlined'}
                sx={{ 
                  borderColor: karnofskyScore === kps ? `${color}.main` : 'divider',
                  borderWidth: karnofskyScore === kps ? 2 : 1,
                  height: '100%'
                }}
              >
                <CardActionArea 
                  onClick={() => handleKarnofskySelect(kps)}
                  sx={{ height: '100%' }}
                >
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography 
                      variant="h4" 
                      color={karnofskyScore === kps ? `${color}.main` : 'text.primary'}
                      fontWeight="bold"
                    >
                      {kps}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      {KARNOFSKY_SCORE_DESCRIPTIONS[kps]?.substring(0, 50)}...
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Selected Summary */}
      {(ecogStatus || karnofskyScore) && (
        <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Selected Performance Status
          </Typography>
          <Grid container spacing={2}>
            {ecogStatus && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2">
                  <strong>ECOG {ecogStatus}:</strong> {ECOG_STATUS_DESCRIPTIONS[ecogStatus]}
                </Typography>
              </Grid>
            )}
            {karnofskyScore && (
              <Grid item xs={12} md={6}>
                <Typography variant="body2">
                  <strong>KPS {karnofskyScore}:</strong> {KARNOFSKY_SCORE_DESCRIPTIONS[karnofskyScore]}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Notes */}
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Assessment Notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Additional observations about patient's functional status..."
        sx={{ mb: 3 }}
      />

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || (!ecogStatus && !karnofskyScore)}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {loading ? 'Saving...' : 'Save Assessment'}
        </Button>
      </Box>
    </>
  );

  // Show message if no patient selected (standalone mode)
  if (!patientId && !embedded) {
    return (
      <Box>
        <PageHeader
          title="Performance Status Assessment"
          subtitle="ECOG and Karnofsky functional status scales"
          icon={<AssessmentIcon />}
          backTo="/oncology"
        />

        <ContentCard>
          <CardContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Please select a patient to record a performance status assessment.
            </Alert>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              To record a performance status assessment, navigate to a patient's profile and use the Oncology tab.
            </Typography>
            <Button variant="outlined" onClick={() => navigate('/patients')}>
              Go to Patients
            </Button>
          </CardContent>
        </ContentCard>
      </Box>
    );
  }

  // Embedded mode (used within patient context)
  if (embedded) {
    return (
      <ContentCard>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <IconButton onClick={() => onCancel ? onCancel() : navigate(-1)} size="small">
              <ArrowBackIcon />
            </IconButton>
            <AssessmentIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Performance Status Assessment
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Assess patient's functional status using ECOG and/or Karnofsky scales.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Performance status assessment saved successfully!
            </Alert>
          )}

          {formContent}
        </CardContent>
      </ContentCard>
    );
  }

  // Standalone page with patient
  return (
    <Box>
      <PageHeader
        title="Performance Status Assessment"
        subtitle="ECOG and Karnofsky functional status scales"
        icon={<AssessmentIcon />}
        backTo="/oncology"
      />

      <ContentCard>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Performance status assessment saved successfully!
            </Alert>
          )}

          {formContent}
        </CardContent>
      </ContentCard>
    </Box>
  );
};

export default PerformanceStatusAssessment;

